import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { PRO_TIER_ENABLED, type Tier, TIER_LIMITS } from "@/lib/featureFlags";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Raw response shape from the check-subscription edge function. */
export interface SubscriptionStatus {
  subscribed: boolean;
  product_id: string | null;
  subscription_end: string | null;
  is_trialing: boolean;
  trial_end: string | null;
  // New fields (returned by updated edge function)
  tier?: Tier;
  hasActiveBoost?: boolean;
  boostExpiresAt?: string | null;
  hasHeavyAI?: boolean;
  aiActionsRemaining?: number | "unlimited";
  isPro?: boolean;
}

/** Resolved entitlements object — the primary public API of this hook. */
export interface UserEntitlements {
  tier: Tier;
  hasActiveBoost: boolean;
  boostExpiresAt: string | null;
  aiActionsRemaining: number | "unlimited";
  canAddAssets: boolean;
  hasHeavyAI: boolean;
  hasLightAI: boolean;
  storageLimit: number;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

const DEFAULT_STORAGE_LIMIT_MB = 100;
const PRO_STORAGE_LIMIT_MB = 500;

const DEFAULT_ENTITLEMENTS: UserEntitlements = {
  tier: "expired",
  hasActiveBoost: false,
  boostExpiresAt: null,
  aiActionsRemaining: 0,
  canAddAssets: false,
  hasHeavyAI: false,
  hasLightAI: false,
  storageLimit: DEFAULT_STORAGE_LIMIT_MB,
};

// ---------------------------------------------------------------------------
// Resolve entitlements from edge function response
// ---------------------------------------------------------------------------

function resolveEntitlements(data: SubscriptionStatus): UserEntitlements {
  // If the edge function already returns the new tier field, use it directly
  if (data.tier) {
    const tier = data.tier;
    const hasActiveBoost = data.hasActiveBoost ?? false;
    const hasHeavyAI = data.hasHeavyAI ?? (tier === "unlimited" || tier === "trial" || hasActiveBoost);
    const hasLightAI = tier !== "expired";
    const limits = TIER_LIMITS[tier];

    return {
      tier,
      hasActiveBoost,
      boostExpiresAt: data.boostExpiresAt ?? null,
      aiActionsRemaining: data.aiActionsRemaining ?? (hasHeavyAI ? "unlimited" : limits.ai_actions_per_month),
      canAddAssets: tier !== "expired",
      hasHeavyAI,
      hasLightAI,
      storageLimit: tier === "vault" || tier === "unlimited" ? PRO_STORAGE_LIMIT_MB : DEFAULT_STORAGE_LIMIT_MB,
    };
  }

  // Backward compat: old edge function only returns subscribed/is_trialing
  if (data.is_trialing) {
    return {
      tier: "trial",
      hasActiveBoost: false,
      boostExpiresAt: null,
      aiActionsRemaining: "unlimited",
      canAddAssets: true,
      hasHeavyAI: true,
      hasLightAI: true,
      storageLimit: DEFAULT_STORAGE_LIMIT_MB,
    };
  }

  if (data.subscribed) {
    return {
      tier: "vault", // assume vault for legacy responses
      hasActiveBoost: false,
      boostExpiresAt: null,
      aiActionsRemaining: TIER_LIMITS.vault.ai_actions_per_month,
      canAddAssets: true,
      hasHeavyAI: false,
      hasLightAI: true,
      storageLimit: PRO_STORAGE_LIMIT_MB,
    };
  }

  return DEFAULT_ENTITLEMENTS;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSubscription() {
  const { user, loading } = useAuth();

  const query = useQuery({
    queryKey: ["subscription", user?.id],
    enabled: PRO_TIER_ENABLED && !loading && !!user,
    refetchInterval: 300_000,
    refetchOnWindowFocus: true,
    retry: 2,
    queryFn: async (): Promise<SubscriptionStatus> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        return {
          subscribed: false,
          product_id: null,
          subscription_end: null,
          is_trialing: false,
          trial_end: null,
        };
      }

      const { data, error } = await supabase.functions.invoke("check-subscription", {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) {
        const status = (error as any)?.context?.status;
        console.error("[useSubscription] Edge function error:", { status, error });
        if (status === 401) {
          return {
            subscribed: false,
            product_id: null,
            subscription_end: null,
            is_trialing: false,
            trial_end: null,
          };
        }
        throw error;
      }
      return data as SubscriptionStatus;
    },
  });

  // Resolve full entitlements
  const entitlements: UserEntitlements = query.data
    ? resolveEntitlements(query.data)
    : DEFAULT_ENTITLEMENTS;

  /**
   * @deprecated Use `entitlements.tier` instead.
   * - `entitlements.tier !== "expired"` — any active subscription (replaces `isPro`)
   * - `entitlements.tier === "trial"` — replaces `isTrial`
   * - `entitlements.tier === "vault"` — paid vault subscriber
   * - `entitlements.tier === "unlimited"` — unlimited tier
   * Kept for backward compatibility; will be removed in a future cleanup pass.
   */
  // Backward compat: isPro = true for any active tier (trial, vault, unlimited)
  const isPro = !PRO_TIER_ENABLED || entitlements.tier !== "expired";
  const isTrial = entitlements.tier === "trial";
  const trialEnd = query.data?.trial_end ?? null;

  return {
    // Legacy API (preserved for backward compat)
    isPro,
    isTrial,
    trialEnd,
    isLoading: PRO_TIER_ENABLED ? query.isLoading : false,
    isError: query.isError,
    subscription: query.data ?? null,
    refetch: query.refetch,

    // New entitlements API
    entitlements,
  };
}
